/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useBusyStateContext } from '#hooks/UNSAFE_useBusyStateContext';
import { useRef, useCallback, useLayoutEffect } from 'preact/hooks';

/**
 * Provides way to add and resolve a busy state.
 */
export function useAddBusyState(description: string) {
  const { addBusyState } = useBusyStateContext();
  const resolverRef = useRef<() => void>();

  const onBusyEnd = useCallback(() => {
    resolverRef.current?.();
    resolverRef.current = undefined;
  }, []);

  const onBusyStart = useCallback(() => {
    // Resolve previous busy state if any
    onBusyEnd();
    resolverRef.current = addBusyState(description);
  }, [description, onBusyEnd, addBusyState]);

  // Resolve any pending busy state on unmount - return onBusyEnd callback itself (do not execute it)
  useLayoutEffect(() => onBusyEnd, [onBusyEnd]);

  return {
    onBusyStart,
    onBusyEnd
  };
}
